import { Button } from 'components/ui/button';
import { podcastServices } from 'features/podcast/services/podcastApi';
import { LoaderIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { TypeAnimation } from 'react-type-animation';
import { RootState } from 'store';
import { regenDialogue } from 'store/slices/podcastResponse';

interface RegenerateMessageProps {
  setOpenRegenerate: (value: boolean) => void;
  regenPart: string;
  voice_name: string;
  index: number;
}

const RegenerateDialogue = ({
  setOpenRegenerate,
  regenPart,
  voice_name,
  index,
}: RegenerateMessageProps) => {
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);
  const { data } = useSelector((state: RootState) => state.podcastRes);
  const [inputPrompt, setInputPrompt] = useState<string>('');
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(false); // to check for mobile

  useEffect(() => {
    if (window.innerWidth < 640) {
      setIsMobile(true);
    }
  }, [window.innerWidth]);
  const [loading, setLoading] = useState(false);

  const handleRegenerate = async () => {
    if (inputPrompt.length < 5 || inputPrompt.length > 300) {
      toast.error('Input prompt can be 5-300 characters only');
      return;
    }

    const regenInput = {
      regenerate_part: regenPart,
      voice_name: voice_name,
      podcast_script: data.dialogues,
      user_prompt: inputPrompt,
    };
    setLoading(true);
    try {
      const res = await podcastServices.regenerateDialogue(regenInput);

      if (res.success) {
        const text = res.data.regenerated_dialogue;
        const url = res.data.presigned_preview_url;
        const preview_url = res.data.preview_url;
        toast.success('Dialogue regenerated!');
        setOpenRegenerate(false);
        dispatch(
          regenDialogue({
            index,
            text,
            presigned_preview_url: url,
            preview_url,
          })
        );
      }
    } catch (error) {
      toast.error('Failed to regenerate dialogue.');
    } finally {
      setLoading(false);
    }
  };

  if (isMobile) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 p-4">
        <div className="bg-background-main rounded-lg shadow-lg p-6 w-full max-w-[90%] sm:max-w-[296px]">
          <h1 className="text-xl text-left font-bold text-text-primary">
            Regenerate dialogue
          </h1>
          <div className=" max-w-[296px] text-left text-[13px] font-normal">
            <input
              title="prompt"
              className="rounded-md mt-2 w-full border-[#D7DAD9] placeholder-[#D7DAD9] focus:outline-none focus:ring-0"
              placeholder="Enter prompt here"
              onChange={(e) => setInputPrompt(e.target.value)}
            />
          </div>
          <TypeAnimation
            sequence={[
              'Eg. Make the tone funny',
              1000,
              'Eg. Turn it into a question',
              2000,
              'Eg. Make it shorter',
            ]}
            wrapper="p"
            cursor={false}
            repeat={0}
            style={{
              fontSize: '14px',
              color: isDarkMode ? '#eeeeee' : '#000A07A0',
              display: 'inline-block',
            }}
          />
          <div className="flex flex-row justify-end mt-3 w-full gap-2">
            <Button
              variant="customSecondary"
              size="sm"
              onClick={() => {
                setOpenRegenerate(false);
                setInputPrompt('');
              }}
              className="text-[14px]"
            >
              Cancel
            </Button>

            <Button
              variant="customPrimary"
              size="sm"
              className="text-[14px]"
              disabled={loading}
              onClick={handleRegenerate}
            >
              {loading && (
                <div className="animate-spin">
                  <LoaderIcon size={20} />
                </div>
              )}
              <span>Regenerate</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute z-10 top-[56px] bg-white rounded-lg shadow-lg py-4 px-4 w-[296px] flex flex-col border-2">
      <div className="flex flex-col gap-1">
        <h1 className=" text-[14px] text-left font-semibold text-[#1A211E]">
          Regenerate dialogue
        </h1>
        <div className=" max-w-[296px] text-left text-[13px] font-normal">
          <input
            title="prompt"
            className="rounded-md mt-2 w-full border-[#D7DAD9] placeholder-[#D7DAD9] focus:outline-none focus:ring-0"
            placeholder="Enter prompt here"
            onChange={(e) => setInputPrompt(e.target.value)}
          />
        </div>
        <TypeAnimation
          sequence={[
            'Eg. Make the tone funny',
            1000,
            'Eg. Turn it into a question',
            2000,
            'Eg. Make it shorter',
          ]}
          wrapper="p"
          cursor={false}
          repeat={0}
          style={{
            fontSize: '14px',
            color: '#000A07A0',
            display: 'inline-block',
          }}
        />
      </div>
      <div className="flex flex-row justify-end mt-3 w-full gap-2">
        <Button
          variant="customSecondary"
          size="sm"
          onClick={() => {
            setOpenRegenerate(false);
            setInputPrompt('');
          }}
          className="text-[14px]"
        >
          Cancel
        </Button>

        <Button
          variant="customPrimary"
          size="sm"
          className="text-[14px]"
          disabled={loading}
          onClick={handleRegenerate}
        >
          {loading && (
            <div className="animate-spin">
              <LoaderIcon size={20} />
            </div>
          )}
          <span>Regenerate</span>
        </Button>
      </div>
    </div>
  );
};

export default RegenerateDialogue;
